import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getVideoById } from '@/mocks/videos';
import { getArticleBySlug, articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  // Try to find as video item first, then as article
  const video = getVideoById(slug || '');
  const article = getArticleBySlug(slug || '');

  const isVideo = !!video;
  const content = video || article;

  if (!content) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-full">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-2">内容未找到</h1>
            <p className="text-sm text-foreground-600 mb-6">抱歉，您查找的内容不存在或已下线</p>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 px-6 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-all cursor-pointer"
            >
              <i className="ri-home-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryLabel = isVideo ? (video as ReturnType<typeof getVideoById>)!.category : (article as ReturnType<typeof getArticleBySlug>)!.category;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: isVideo ? (video as ReturnType<typeof getVideoById>)!.title : (article as ReturnType<typeof getArticleBySlug>)!.title,
    description: isVideo ? (video as ReturnType<typeof getVideoById>)!.description : (article as ReturnType<typeof getArticleBySlug>)!.excerpt,
    image: isVideo ? (video as ReturnType<typeof getVideoById>)!.thumbnail : (article as ReturnType<typeof getArticleBySlug>)!.coverImage,
    datePublished: isVideo ? `${(video as ReturnType<typeof getVideoById>)!.year}-01-01` : (article as ReturnType<typeof getArticleBySlug>)!.publishDate,
    author: {
      '@type': 'Person',
      name: isVideo ? (video as ReturnType<typeof getVideoById>)!.director || '桃花视频' : (article as ReturnType<typeof getArticleBySlug>)!.author,
    },
  };

  // Related items
  const relatedVideos = isVideo
    ? articles.filter((a) => a.isFeatured).slice(0, 3)
    : [];

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>

      <Navbar />

      <main className="pt-16">
        {/* Breadcrumb */}
        <div className="bg-background-50 border-b border-background-200/70">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: categoryLabel },
              { label: isVideo ? (video as ReturnType<typeof getVideoById>)!.title : (article as ReturnType<typeof getArticleBySlug>)!.title },
            ]}
          />
        </div>

        <article className="w-full px-4 md:px-6 lg:px-8 py-8 md:py-10 bg-background-50">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <header className="mb-8">
              <span className="inline-block px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full mb-4">
                {categoryLabel}
              </span>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
                {isVideo ? (video as ReturnType<typeof getVideoById>)!.title : (article as ReturnType<typeof getArticleBySlug>)!.title}
              </h1>

              {isVideo ? (
                <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-600">
                  <span className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-500"></i>
                    <strong>{(video as ReturnType<typeof getVideoById>)!.rating}</strong>
                  </span>
                  <span>·</span>
                  <span>{(video as ReturnType<typeof getVideoById>)!.year}</span>
                  <span>·</span>
                  <span>{(video as ReturnType<typeof getVideoById>)!.region}</span>
                  <span>·</span>
                  <span>{(video as ReturnType<typeof getVideoById>)!.source}</span>
                </div>
              ) : (
                <div className="flex items-center gap-3 text-sm text-foreground-600">
                  <span>{(article as ReturnType<typeof getArticleBySlug>)!.author}</span>
                  <span>·</span>
                  <span>{(article as ReturnType<typeof getArticleBySlug>)!.publishDate}</span>
                  <span>·</span>
                  <span>{(article as ReturnType<typeof getArticleBySlug>)!.readTime}阅读</span>
                </div>
              )}
            </header>

            {/* Cover Image */}
            <div className="rounded-xl overflow-hidden mb-8">
              <img
                src={isVideo ? (video as ReturnType<typeof getVideoById>)!.thumbnail : (article as ReturnType<typeof getArticleBySlug>)!.coverImage}
                alt={isVideo ? (video as ReturnType<typeof getVideoById>)!.title : (article as ReturnType<typeof getArticleBySlug>)!.title}
                className="w-full aspect-video object-cover"
              />
            </div>

            {/* Video Meta Info */}
            {isVideo && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
                <div className="bg-background-100 rounded-lg p-3">
                  <p className="text-xs text-foreground-500 mb-1">导演</p>
                  <p className="text-sm font-medium text-foreground-900">{(video as ReturnType<typeof getVideoById>)!.director || '未知'}</p>
                </div>
                <div className="bg-background-100 rounded-lg p-3">
                  <p className="text-xs text-foreground-500 mb-1">主演</p>
                  <p className="text-sm font-medium text-foreground-900 line-clamp-1">
                    {(video as ReturnType<typeof getVideoById>)!.actors.join('、') || '未知'}
                  </p>
                </div>
                <div className="bg-background-100 rounded-lg p-3">
                  <p className="text-xs text-foreground-500 mb-1">地区</p>
                  <p className="text-sm font-medium text-foreground-900">{(video as ReturnType<typeof getVideoById>)!.region}</p>
                </div>
                <div className="bg-background-100 rounded-lg p-3">
                  <p className="text-xs text-foreground-500 mb-1">更新状态</p>
                  <p className="text-sm font-medium text-foreground-900">{(video as ReturnType<typeof getVideoById>)!.updateInfo}</p>
                </div>
              </div>
            )}

            {/* Article Content */}
            <div className="prose-custom">
              {isVideo ? (
                <div>
                  <h2 className="text-xl font-heading font-bold text-foreground-950 mb-3">剧情简介</h2>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-6">
                    {(video as ReturnType<typeof getVideoById>)!.description}
                  </p>
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    本片由{(video as ReturnType<typeof getVideoById>)!.source}提供高清在线播放服务，
                    桃花视频为您聚合全网优质影视资源，让您畅享流畅不缓冲的观影体验。
                    更多精彩电视剧、电影、综艺、动漫、纪录片尽在桃花视频，满足您全方位的追剧需求。
                  </p>
                </div>
              ) : (
                <div
                  className="text-sm text-foreground-700 leading-relaxed space-y-4 [&_h2]:text-xl [&_h2]:font-heading [&_h2]:font-bold [&_h2]:text-foreground-950 [&_h2]:mt-8 [&_h2]:mb-4 [&_p]:leading-7"
                  dangerouslySetInnerHTML={{ __html: (article as ReturnType<typeof getArticleBySlug>)!.contentHtml }}
                />
              )}
            </div>

            {/* Tags */}
            {!isVideo && (
              <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-background-200/70">
                {(article as ReturnType<typeof getArticleBySlug>)!.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700 cursor-pointer hover:bg-secondary-200 transition-colors whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            )}

            {/* Related Articles */}
            {isVideo && relatedVideos.length > 0 && (
              <div className="mt-12 pt-8 border-t border-background-200/70">
                <h2 className="text-xl font-heading font-bold text-foreground-950 mb-5">相关影视资讯</h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedVideos.map((art) => (
                    <Link
                      key={art.id}
                      to={`/article/${art.slug}`}
                      className="group bg-background-100 rounded-xl overflow-hidden cursor-pointer transition-all hover:scale-[1.02]"
                    >
                      <div className="aspect-video overflow-hidden">
                        <img
                          src={art.coverImage}
                          alt={art.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                      </div>
                      <div className="p-3">
                        <h3 className="font-heading font-semibold text-foreground-950 text-sm mb-1 line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {art.title}
                        </h3>
                        <span className="text-xs text-foreground-500">{art.publishDate}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}